import jmespath from "jmespath";
import jsonata from "jsonata";
import { useContext, useEffect, useState, useCallback } from "react";
import FieldMappingModalContext from "../../../context/FieldMappingModalContext";
import useAppContext from "../../../../../../../../context/useAppContext";
import { containsOnlyAllowedJsonataFunctions, JsonValue } from "../../../../utils/expressionUtils";

type UseAdvancedFieldMappingsSelectionParams = {
  onJsonAdvancedMappingExpressionChange: (jsonAdvancedMappingExpression: string | null) => void;
  jsonData: JsonValue;
};

const useAdvancedFieldMappingSelection = ({
  onJsonAdvancedMappingExpressionChange,
  jsonData,
}: UseAdvancedFieldMappingsSelectionParams) => {
  const [inputError, setInputError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [jsonAdvancedMappingExpressionResult, setJsonAdvancedMappingExpressionResult] = useState<
    string | null
  >(null);
  const [isJsonAdvancedMapppingExpressionValid, setIsJsonAdvancedMapppingExpressionValid] =
    useState(false);

  const { jsonAdvancedMappingExpression, linkedAccount, selectedRemoteField } =
    useContext(FieldMappingModalContext);
  const { user } = useAppContext();
  const isJmesPathDeprecationEnabled = user?.is_jmes_path_deprecation_dashboard_enabled ?? false;

  const evaluateExpression = useCallback(
    async (
      path: string,
    ): Promise<{ result: string | null; isValid: boolean; errorMessage: string | null }> => {
      try {
        if (isJmesPathDeprecationEnabled) {
          // Check for disallowed functions
          if (!containsOnlyAllowedJsonataFunctions(path)) {
            return {
              result: null,
              isValid: false,
              errorMessage: "Invalid expression - please verify the expression",
            };
          }

          const expression = jsonata(path);
          const rawResult = await expression.evaluate(jsonData);
          // Allow expressions that don't match the example data - they may be valid for other records
          const result = rawResult === undefined ? null : JSON.stringify(rawResult);
          return { result, isValid: true, errorMessage: null };
        } else {
          const result = JSON.stringify(jmespath.search(jsonData, path));
          return { result, isValid: true, errorMessage: null };
        }
      } catch (e) {
        const message = (e as { message?: string })?.message ?? "Invalid expression";
        return { result: null, isValid: false, errorMessage: message };
      }
    },
    [jsonData, isJmesPathDeprecationEnabled],
  );

  // Sync context `jsonAdvancedMappingExpression` with local state
  useEffect(() => {
    if (jsonAdvancedMappingExpression) {
      evaluateExpression(jsonAdvancedMappingExpression).then(
        ({ result, isValid, errorMessage: evalErrorMessage }) => {
          setInputError(!isValid);
          setErrorMessage(evalErrorMessage);
          setIsJsonAdvancedMapppingExpressionValid(isValid);
          setJsonAdvancedMappingExpressionResult(result);
        },
      );
    } else {
      // Reset error state when expression is cleared externally
      setInputError(false);
      setErrorMessage(null);
      setIsJsonAdvancedMapppingExpressionValid(false);
      setJsonAdvancedMappingExpressionResult(null);
    }
  }, [jsonAdvancedMappingExpression, evaluateExpression]);

  // Handle expression input changes (JMESPath or JSONata based on flag)
  const handleJsonAdvancedMappingExpressionInput = useCallback(
    async (input: string) => {
      if (input.trim() === "") {
        // Reset state for empty input
        setInputError(false);
        setErrorMessage(null);
        setIsJsonAdvancedMapppingExpressionValid(false);
        setJsonAdvancedMappingExpressionResult(null);
        onJsonAdvancedMappingExpressionChange(null);
        return;
      }

      const { result, isValid, errorMessage: evalErrorMessage } = await evaluateExpression(input);

      // Update state based on evaluation
      setInputError(!isValid);
      setErrorMessage(evalErrorMessage);
      setIsJsonAdvancedMapppingExpressionValid(isValid);
      setJsonAdvancedMappingExpressionResult(result);

      // Notify parent of expression change
      onJsonAdvancedMappingExpressionChange(input);
    },
    [evaluateExpression, onJsonAdvancedMappingExpressionChange],
  );

  return {
    inputError,
    errorMessage,
    handleJsonAdvancedMappingExpressionInput,
    jsonAdvancedMappingExpressionResult,
    isJsonAdvancedMapppingExpressionValid,
  };
};

export default useAdvancedFieldMappingSelection;
